import React from 'react';
import { isItemApplicable } from '@skd/shared';

interface CarBlueprintProps {
  stageId: number;
  itemStatuses: Record<string, 'OK' | 'NG' | 'NA'>;
  onHotspotClick: (itemId: string) => void;
}

export function CarBlueprint({ stageId, itemStatuses, onHotspotClick }: CarBlueprintProps) {
  const getHotspotClass = (itemId: string) => {
    if (!isItemApplicable(itemId, stageId)) return 'opacity-30 cursor-not-allowed';
    const status = itemStatuses[itemId];
    if (status === 'OK') return 'cursor-pointer [&_.hotspot-path]:fill-[var(--status-ok-bg)] [&_.hotspot-path]:stroke-[var(--status-ok-border)] [&_.hotspot-rect]:fill-[var(--status-ok-bg)] [&_.hotspot-rect]:stroke-[var(--status-ok-border)]';
    if (status === 'NG') return 'cursor-pointer animate-pulse-red [&_.hotspot-path]:fill-[var(--status-ng-bg)] [&_.hotspot-path]:stroke-[var(--status-ng-border)] [&_.hotspot-rect]:fill-[var(--status-ng-bg)] [&_.hotspot-rect]:stroke-[var(--status-ng-border)]';
    return 'cursor-pointer';
  };

  const handleClick = (itemId: string) => {
    if (isItemApplicable(itemId, stageId)) {
      onHotspotClick(itemId);
    }
  };

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl p-4 overflow-x-auto">
      <svg viewBox="0 0 900 450" className="w-full max-w-[900px] mx-auto">
        <defs>
          <linearGradient id="car-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--car-body-start, #1e293b)"/>
            <stop offset="100%" stopColor="var(--car-body-end, #0f172a)"/>
          </linearGradient>
        </defs>
        <rect x="20" y="20" width="860" height="410" rx="16" fill="var(--card-bg-subtle)" stroke="var(--border-color)" strokeWidth="1.5" />

        {/* Top View Group */}
        <g transform="translate(450, 130)">
          <text x="0" y="-85" textAnchor="middle" className="text-xs fill-[var(--text-dim)] font-mono">TOP VIEW COMPARTMENTS</text>
          <path d="M -160,-65 C -130,-75 130,-75 160,-65 C 180,-30 180,30 160,65 C 130,75 -130,75 -160,65 C -180,30 -180,-30 -160,-65 Z" fill="url(#car-grad)" stroke="var(--car-stroke)" strokeWidth="2"/>

          <g className={`hotspot-zone ${getHotspotClass('f')}`} onClick={() => handleClick('f')}>
            <path d="M -150,-45 L -95,-48 L -95,48 L -150,45 Z" className="hotspot-path fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="-120" y="5" textAnchor="middle" className="text-[10px] fill-[var(--text-muted)] font-mono pointer-events-none">(f) HOOD</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('i')}`} onClick={() => handleClick('i')}>
            <rect x="-30" y="-45" width="95" height="90" rx="8" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="17" y="5" textAnchor="middle" className="text-[10px] fill-[var(--text-muted)] font-mono pointer-events-none">(i) BODY / ROOF</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('e')}`} onClick={() => handleClick('e')}>
            <path d="M 125,-42 L 155,-35 L 155,35 L 125,42 Z" className="hotspot-path fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="140" y="5" textAnchor="middle" className="text-[10px] fill-[var(--text-muted)] font-mono pointer-events-none">(e) TAILGATE</text>
          </g>
        </g>

        {/* Side View Group */}
        <g transform="translate(250, 310)">
          <text x="0" y="-75" textAnchor="middle" className="text-xs fill-[var(--text-dim)] font-mono">SIDE VIEW COMPARTMENTS</text>
          <path d="M -170,15 L -165,-15 C -150,-25 -100,-40 -50,-50 L 50,-50 C 90,-50 120,-20 150,-15 L 165,15 C 170,30 160,35 150,35 L -150,35 C -165,35 -175,25 -170,15 Z" fill="url(#car-grad)" stroke="var(--car-stroke)" strokeWidth="2"/>
          <circle cx="-100" cy="35" r="22" fill="#0f172a" stroke="var(--accent-color)" strokeWidth="3"/>
          <circle cx="100" cy="35" r="22" fill="#0f172a" stroke="var(--accent-color)" strokeWidth="3"/>

          <g className={`hotspot-zone ${getHotspotClass('b')}`} onClick={() => handleClick('b')}>
            <rect x="-45" y="-45" width="42" height="65" rx="4" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="-24" y="-10" textAnchor="middle" className="text-[9px] fill-[var(--text-muted)] font-mono pointer-events-none">(b) FRONT DOOR L</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('d')}`} onClick={() => handleClick('d')}>
            <rect x="2" y="-45" width="42" height="65" rx="4" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="23" y="-10" textAnchor="middle" className="text-[9px] fill-[var(--text-muted)] font-mono pointer-events-none">(d) REAR DOOR L</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('h')}`} onClick={() => handleClick('h')}>
            <path d="M -155,10 L -120,5 L -90,-25 L -140,-15 Z" className="hotspot-path fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="-125" y="-3" textAnchor="middle" className="text-[9px] fill-[var(--text-muted)] font-mono pointer-events-none">(h) FENDER L</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('g')}`} onClick={() => handleClick('g')}>
            <path d="M -155,25 L -120,20 L -90,-10 L -140,-5 Z" className="hotspot-path fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="-125" y="15" textAnchor="middle" className="text-[8px] fill-[var(--text-muted)] font-mono pointer-events-none">(g) FENDER R</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('a')}`} onClick={() => handleClick('a')}>
            <rect x="-45" y="25" width="42" height="10" rx="2" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="-24" y="33" textAnchor="middle" className="text-[8px] fill-[var(--text-muted)] font-mono pointer-events-none">(a) FRONT DOOR R</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('c')}`} onClick={() => handleClick('c')}>
            <rect x="2" y="25" width="42" height="10" rx="2" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="23" y="33" textAnchor="middle" className="text-[8px] fill-[var(--text-muted)] font-mono pointer-events-none">(c) REAR DOOR R</text>
          </g>
        </g>

        {/* Auxiliary Views Group */}
        <g transform="translate(680, 310)">
          <text x="0" y="-75" textAnchor="middle" className="text-xs fill-[var(--text-dim)] font-mono">AUXILIARY ITEMS</text>

          <g className={`hotspot-zone ${getHotspotClass('j')}`} onClick={() => handleClick('j')}>
            <rect x="-55" y="-30" width="110" height="55" rx="8" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="0" y="3" textAnchor="middle" className="text-[10px] fill-[var(--text-muted)] font-mono pointer-events-none">(j) CASE COND.</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('k')}`} onClick={() => handleClick('k')}>
            <rect x="-55" y="35" width="110" height="40" rx="8" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="0" y="60" textAnchor="middle" className="text-[10px] fill-[var(--text-muted)] font-mono pointer-events-none">(k) WOOD PALLET</text>
          </g>
          <g className={`hotspot-zone ${getHotspotClass('l')}`} onClick={() => handleClick('l')}>
            <rect x="-55" y="85" width="110" height="40" rx="8" className="hotspot-rect fill-[var(--accent-light)] stroke-[var(--accent-color)] stroke-[1.5]"/>
            <text x="0" y="110" textAnchor="middle" className="text-[10px] fill-[var(--text-muted)] font-mono pointer-events-none">(l) SKID COND.</text>
          </g>
        </g>
      </svg>
    </div>
  );
}
